import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { X } from 'lucide-react';

export const ScheduleConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedDate,
  selectedTime,
  pendingInstances,
  setAppointmentNotes
}) => {
  const [checkedIds, setCheckedIds] = useState(new Set());
  // const [isConfirmed]

  useEffect(() => {
    if (isOpen) {
      setCheckedIds(new Set(pendingInstances.map(i => i.instanceId)));
    }
  }, [isOpen, pendingInstances]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const isSinglePending = pendingInstances.length === 1;
  const checkedCount = checkedIds.size;

  const handleToggle = (instanceId) => {
    if (isSinglePending) return;

    const newChecked = new Set(checkedIds);
    if (newChecked.has(instanceId)) {
      if (newChecked.size <= 1) return;
      newChecked.delete(instanceId);
    } else {
      newChecked.add(instanceId);
    }
    setCheckedIds(newChecked);
  };

  const handleConfirm = () => {
    onConfirm(Array.from(checkedIds));
  };
  console.log('selectedTime', selectedTime)
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm scheduling"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-lg font-bold text-gray-800 mb-1">
          Select the services you'd like to schedule
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          on {format(selectedDate, 'MMMM d, yyyy')} - {selectedTime === 'full_day' ? '7:00 AM - 5:00 PM' : (selectedTime === 'AM' ? '7:00 AM - 12:00 PM' : '12:00 PM - 5:00 PM')}
        </p>

        <div className="space-y-3 mb-2">
          {pendingInstances.map((instance) => {
            const isChecked = checkedIds.has(instance.instanceId);
            const isDisabled = isSinglePending || (isChecked && checkedCount <= 1);

            return (
              <label
                key={instance.instanceId}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${isChecked
                    ? "bg-green-50 border-green-300"
                    : "bg-gray-50 border-gray-200"} ${!isDisabled && "cursor-pointer hover:border-green-400"} ${isDisabled && "cursor-default"}`
                }
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleToggle(instance.instanceId)}
                  disabled={isDisabled}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
                />
                <span className={`text-sm font-medium ${isChecked ? "text-green-800" : "text-gray-500"}`}>
                  {instance.serviceName}
                </span>
              </label>
            );
          })}
        </div>
        {!isSinglePending && (
          <p className="text-xs text-gray-400 mb-4">
            Uncheck services you want on a different day
          </p>
        )}
        {isSinglePending && <div className="mb-4" />}
        
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-4">
            <textarea
              placeholder="Notes for the appointment"
              className="w-full h-24 resize-none text-stone-700 text-sm leading-relaxed placeholder-stone-300 outline-none"
              onChange={(e) => setAppointmentNotes(e.target.value)}
            />
          </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={checkedCount === 0}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors text-sm ${checkedCount > 0
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"}`
            }
          >
            Confirm Schedule
          </button>
        </div>
      </div>
    </div>
  );
};
